import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ChatModal from '../../components/ChatModal/ChatModal';
import LoginModal from '../../components/LoginModal/LoginModal';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useInquiry } from '../../context/InquiryContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { API_BASE_URL } from '../../config/api';
import { appendMobileBrowsingProduct } from '../../utils/mobileBrowsingHistory';
import { getDeliverLocal } from '../../utils/mobileSettingsStorage';
import { productFavoritesApi } from '../../services/apiService';
import MobileProductVariationsModal from './MobileProductVariationsModal';
import './MobileProductDetailsPage.css';

const LOGO_PH = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=120';

function getImageList(p) {
  if (!p) return [];
  if (p.productImages?.length) return p.productImages.map((x) => x.url).filter(Boolean);
  let arr = [];
  if (p.images) {
    try {
      arr = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
    } catch {
      arr = [];
    }
  }
  if (arr?.length) return arr.filter(Boolean);
  return [p.image || LOGO_PH];
}

function buildSpecs(p) {
  if (!p) return [];
  const base = [
    { label: 'Color', value: p.color },
    { label: 'Material', value: p.material },
    { label: 'Size', value: p.size },
    { label: 'Net Weight', value: p.netWeight }
  ].filter((x) => x.value && String(x.value).trim());
  let custom = [];
  try {
    if (p.customSpecs) {
      const raw = typeof p.customSpecs === 'string' ? JSON.parse(p.customSpecs) : p.customSpecs;
      if (Array.isArray(raw)) custom = raw.filter((s) => s.key && s.value).map((s) => ({ label: s.key, value: s.value }));
    }
  } catch {
    /* ignore */
  }
  return [...base, ...custom].slice(0, 8);
}

function priceTiers(p, formatPrice) {
  const min = Number(p?.priceMin) || 0;
  const max = Number(p?.priceMax) || min;
  const mid = min + (max - min) * 0.45;
  const hi = max;
  const moq = p?.moq || '1 Unit';
  return [
    { price: min, range: `Min. order: ${moq}` },
    { price: mid, range: '1,000–9,999 units' },
    { price: hi, range: '10,000+ units' }
  ].map((t) => ({ ...t, label: formatPrice(t.price) }));
}

export default function MobileProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrice, formatPriceRange, selectedCurrency } = useCurrency();
  const { isAuthenticated, user } = useAuth();
  const { addToInquiry, isInInquiry } = useInquiry();
  const { hasPermission } = useSubscription();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQ, setSearchQ] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [isFav, setIsFav] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  const [variationsOpen, setVariationsOpen] = useState(false);

  const overviewRef = useRef(null);
  const detailsRef = useRef(null);
  const recommendRef = useRef(null);

  const images = useMemo(() => getImageList(product), [product]);
  const seller = product?.seller || {};
  const specs = useMemo(() => buildSpecs(product), [product]);
  const tiers = useMemo(() => (product ? priceTiers(product, formatPrice) : []), [product, formatPrice]);

  const deliverCode = getDeliverLocal()?.countryCode || 'IN';

  const reviews = useMemo(() => {
    const list = seller?.sellerReviews || [];
    return list.slice(0, 4).map((r) => ({
      id: r.id,
      name: r.reviewerName || 'Buyer',
      rating: r.rating || 5,
      comment: r.comment || '',
      flag: 'https://flagcdn.com/w40/kz.png'
    }));
  }, [seller]);

  const avgRating = useMemo(() => {
    const list = seller?.sellerReviews || [];
    if (!list.length) return '5.0';
    const s = list.reduce((a, r) => a + (r.rating || 5), 0);
    return (s / list.length).toFixed(1);
  }, [seller]);

  const fetchProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/products/${id}`);
      if (res.status === 404) {
        setNotFound(true);
        setProduct(null);
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProduct(data);
      appendMobileBrowsingProduct(data);
      setNotFound(false);
      const rr = await fetch(`${API_BASE_URL}/products/${id}/related?limit=24`);
      if (rr.ok) {
        const list = await rr.json();
        setRelated((list || []).filter((x) => x.status === 'published' && x.id !== data.id));
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
    window.scrollTo(0, 0);
  }, [fetchProduct]);

  useEffect(() => {
    setVariationsOpen(false);
  }, [id]);

  useEffect(() => {
    const run = async () => {
      if (!user?.id || !product?.id) return;
      try {
        const r = await productFavoritesApi.checkFavorite(user.id, product.id);
        setIsFav(r.isFavorited);
      } catch {
        /* ignore */
      }
    };
    run();
  }, [user?.id, product?.id]);

  const scrollTo = (tab) => {
    setActiveTab(tab);
    const map = { overview: overviewRef, details: detailsRef, recommended: recommendRef };
    map[tab]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleChat = () => {
    if (!isAuthenticated) {
      setLoginOpen(true);
      return;
    }
    if (!hasPermission('canLiveChat')) {
      setUpgradeFeature('Live Chat');
      setUpgradeOpen(true);
      return;
    }
    setChatOpen(true);
  };

  const handleStartOrder = () => {
    if (!isAuthenticated) {
      setLoginOpen(true);
      return;
    }
    if (!product) return;
    navigate(`/send-inquire/${product.id}`, {
      state: {
        product: {
          id: product.id,
          image: images[0],
          name: product.name,
          description: product.description,
          priceMin: product.priceMin,
          priceMax: product.priceMax,
          unit: product.unit,
          moq: product.moq,
          sellerId: product.sellerId,
          userId: product.userId,
          seller: product.seller,
          user: product.user
        },
        seller: {
          id: seller.id,
          userId: seller.userId || product.userId,
          name: seller.name,
          logo: seller.logo
        }
      }
    });
  };

  const inquiryPayload = useCallback(() => {
    if (!product) return null;
    return {
      id: product.id,
      image: images[0],
      description: product.description || product.name,
      priceMin: product.priceMin,
      priceMax: product.priceMax,
      priceUnit: selectedCurrency?.code || 'USD',
      pricePer: product.unit || 'Unit',
      moq: product.moq || '1 Unit',
      companyName: seller.name || 'Supplier',
      sellerId: seller.userId || product.userId,
      sellerName: seller.name,
      sellerAvatar: seller.logo || LOGO_PH,
      country: seller.country || 'UAE',
      countryFlag: seller.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
  }, [product, images, seller, selectedCurrency]);

  const handleAddCart = () => {
    if (!isAuthenticated) {
      setLoginOpen(true);
      return;
    }
    setVariationsOpen(true);
  };

  const handleVariationsConfirm = useCallback(
    (opts) => {
      const pl = inquiryPayload();
      if (!pl) return;
      addToInquiry({
        ...pl,
        quantity: opts.quantity,
        variationColor: opts.color,
        variationShipping: opts.shipping,
        estimatedLineUsd: opts.lineTotalUsd
      });
    },
    [addToInquiry, inquiryPayload]
  );

  const toggleFav = async () => {
    if (!isAuthenticated) {
      setLoginOpen(true);
      return;
    }
    if (!product?.id || favBusy) return;
    setFavBusy(true);
    try {
      const r = await productFavoritesApi.toggle(user.id, product.id);
      setIsFav(r.isFavorited);
    } catch {
      /* ignore */
    } finally {
      setFavBusy(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: product?.name, url });
      else await navigator.clipboard.writeText(url);
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="mpd-page mpd-page--center">
        <p className="mpd-muted">Loading…</p>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="mpd-page mpd-page--center">
        <p className="mpd-muted">Product not found.</p>
        <button type="button" className="mpd-btn-outline" onClick={() => navigate('/')}>
          Back home
        </button>
      </div>
    );
  }

  const mainImg = images[imgIdx] || images[0];

  return (
    <div className="mpd-page">
      <header className="mpd-top">
        <button type="button" className="mpd-iconbtn" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="mpd-search">
          <span className="mpd-search-ico">🎮</span>
          <input
            className="mpd-search-inp"
            placeholder="Search products..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchQ.trim()) navigate(`/?q=${encodeURIComponent(searchQ.trim())}`);
            }}
          />
        </div>
        <button type="button" className="mpd-iconbtn" aria-label="Cart" onClick={() => navigate('/mobile/cart')}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="20" r="1.5" />
            <circle cx="17" cy="20" r="1.5" />
            <path d="M3 3h2l2 12h12l2-8H7" />
          </svg>
        </button>
      </header>

      <div className="mpd-gallery">
        <img src={mainImg} alt="" className="mpd-hero-img" />
        <button type="button" className="mpd-heart" onClick={toggleFav} disabled={favBusy} aria-label="Favorite">
          <svg width="22" height="22" viewBox="0 0 24 24" fill={isFav ? '#ff4d4f' : 'none'} stroke={isFav ? '#ff4d4f' : '#fff'} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
          </svg>
        </button>
        <div className="mpd-photo-pill">
          <button type="button" className={imgIdx === 0 ? 'mpd-pill-on' : ''} onClick={() => setImgIdx(0)}>
            Photos {imgIdx + 1}/{images.length}
          </button>
          <button type="button" className="mpd-pill-off">Highlights</button>
        </div>
        {images.length > 1 && (
          <div className="mpd-thumb-row">
            {images.map((u, i) => (
              <button key={i} type="button" className={i === imgIdx ? 'mpd-thumb mpd-thumb--on' : 'mpd-thumb'} onClick={() => setImgIdx(i)}>
                <img src={u} alt="" />
              </button>
            ))}
          </div>
        )}
      </div>

      <section className="mpd-block">
        {product.status === 'published' && <span className="mpd-badge-blue">Ready to Ship</span>}
        <div className="mpd-promo">180-day lowest prices</div>
        <div className="mpd-tiers">
          {tiers.map((t, i) => (
            <div key={i} className="mpd-tier">
              <div className="mpd-tier-price">{t.label}</div>
              <div className="mpd-tier-range">{t.range}</div>
            </div>
          ))}
        </div>
        <div className="mpd-sample-row">
          <span>Sample price: {formatPrice(product.priceMin)}</span>
          <button type="button" className="mpd-sample-btn" onClick={handleStartOrder}>
            Get sample
          </button>
        </div>
        <div className="mpd-title-row">
          <h1 className="mpd-title">{product.name}</h1>
          <button type="button" className="mpd-share" onClick={share} aria-label="Share">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </button>
        </div>
        <p className="mpd-submeta">
          <span>{product.views != null ? `${product.views}+ sold` : '500 sold'}</span>
          <span className="mpd-dot">·</span>
          <span>Store rating: {avgRating} ({seller?.sellerReviews?.length || 108})</span>
        </p>
        <div className="mpd-tags">
          {product.color && <span className="mpd-tag-gold">Customizable</span>}
          <span className="mpd-tag-rank">#{Math.min(99, product.id % 9 + 1)} in category</span>
        </div>
      </section>

      <section className="mpd-supplier">
        <img className="mpd-sup-logo" src={seller.logo || LOGO_PH} alt="" />
        <div className="mpd-sup-txt">
          <div className="mpd-sup-name">Supplier: {(seller.name || 'Verified supplier').slice(0, 42)}…</div>
          <div className="mpd-sup-line">Supplier reorder rate: 33%</div>
          <div className="mpd-sup-line">{seller.memberYears || 7} yrs on platform · 93.6% positive reviews</div>
          <div className="mpd-online">
            <span className="mpd-dot-g" /> Online
          </div>
        </div>
        <Link to={`/seller/${seller.slug || seller.id}`} className="mpd-sup-go">
          ›
        </Link>
      </section>

      <nav className="mpd-tabs">
        {['overview', 'details', 'recommended'].map((t) => (
          <button
            key={t}
            type="button"
            className={activeTab === t ? 'mpd-tab mpd-tab--on' : 'mpd-tab'}
            onClick={() => scrollTo(t)}
          >
            {t === 'overview' ? 'Overview' : t === 'details' ? 'Details' : 'Recommended'}
          </button>
        ))}
      </nav>

      <section ref={overviewRef} className="mpd-sec mpd-anchor">
        <h3 className="mpd-sec-h">Supplier</h3>
        <p className="mpd-sec-p">{(seller.name || 'Supplier').slice(0, 80)}</p>
        <p className="mpd-sec-sub">Supplier reorder rate: 33% · {seller.memberYears || 7} yrs · 93.6% positive review</p>
      </section>

      {product.color && (
        <section className="mpd-sec">
          <h3 className="mpd-sec-h">color (1)</h3>
          <button type="button" className="mpd-color-chip">
            {product.color}
          </button>
        </section>
      )}

      <section className="mpd-sec">
        <h3 className="mpd-sec-h">
          Deliver to <strong>{deliverCode}</strong>
        </h3>
        <div className="mpd-ship-scroll">
          <div className="mpd-ship-card mpd-ship-card--on">
            <div className="mpd-ship-t">Standard</div>
            <div className="mpd-ship-p">{formatPrice(48.5)} for {product.moq || '100 pairs'}</div>
            <div className="mpd-ship-g">Guaranteed delivery</div>
          </div>
          <div className="mpd-ship-card">
            <div className="mpd-ship-t">Premium</div>
            <div className="mpd-ship-p">{formatPrice(62)}+</div>
          </div>
        </div>
      </section>

      <section className="mpd-sec">
        <h3 className="mpd-sec-h">Order protection</h3>
        <p className="mpd-sec-sub">Secure payments · Guaranteed delivery · Money-back · 24/7 support</p>
      </section>

      {specs.length > 0 && (
        <section className="mpd-sec">
          <h3 className="mpd-sec-h">Key attributes</h3>
          <div className="mpd-attr-grid">
            {specs.map((s) => (
              <div key={s.label} className="mpd-attr-cell">
                <div className="mpd-attr-v">{String(s.value).slice(0, 28)}</div>
                <div className="mpd-attr-k">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mpd-sec">
        <h3 className="mpd-sec-h">Customization options</h3>
        <ul className="mpd-checks">
          <li>Customized logo</li>
          <li>Customized packaging</li>
          <li>Graphic customization</li>
        </ul>
      </section>

      <section className="mpd-sec">
        <h3 className="mpd-sec-h">Store reviews</h3>
        <div className="mpd-stars-big">{avgRating} ★★★★★</div>
        {reviews.length === 0 && <p className="mpd-sec-sub">No reviews yet.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="mpd-review">
            <img className="mpd-flag" src={r.flag} alt="" />
            <div>
              <div className="mpd-review-top">
                <strong>{r.name}</strong>
                <span>{'★'.repeat(r.rating)}</span>
              </div>
              {r.comment && <p className="mpd-review-txt">{r.comment}</p>}
            </div>
          </div>
        ))}
      </section>

      <section className="mpd-sec">
        <h3 className="mpd-sec-h">Company overview</h3>
        <p className="mpd-sec-p">{(seller.name || 'Company').slice(0, 60)}…</p>
        <p className="mpd-sec-sub">{seller.businessType || 'Manufacturer'} · {seller.memberYears || 7} yrs · Located in {(seller.country || 'CN').slice(0, 2)}</p>
        <div className="mpd-stat-row">
          <div>
            <strong>75%</strong>
            <div className="mpd-stat-l">On-time delivery</div>
          </div>
          <div>
            <strong>≤4h</strong>
            <div className="mpd-stat-l">Response time</div>
          </div>
        </div>
        <div className="mpd-row-btns">
          <Link to={`/seller/${seller.slug || seller.id}`} className="mpd-outline-btn">
            More products
          </Link>
          <Link to={`/seller/${seller.slug || seller.id}`} className="mpd-outline-btn">
            Company profile
          </Link>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mpd-sec">
          <h3 className="mpd-sec-h">Similar products</h3>
          <div className="mpd-hscroll">
            {related.slice(0, 8).map((rp) => (
              <button key={rp.id} type="button" className="mpd-mini" onClick={() => navigate(`/mobile/product/${rp.id}`)}>
                <img src={rp.image || LOGO_PH} alt="" />
                <span>{formatPriceRange(rp.priceMin, rp.priceMax)}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section ref={detailsRef} className="mpd-sec mpd-anchor">
        <div className="mpd-desc-hdr">
          <span>Product Description</span>
        </div>
        <p className="mpd-desc">{product.description}</p>
        {images.map((u, i) => (
          <img key={i} src={u} alt="" className="mpd-desc-img" />
        ))}
      </section>

      {related.length > 0 && (
        <section ref={recommendRef} className="mpd-sec mpd-anchor">
          <h3 className="mpd-sec-h">Recommended</h3>
          <div className="mpd-rec-grid">
            {related.map((rp) => (
              <button key={rp.id} type="button" className="mpd-rec-card" onClick={() => navigate(`/mobile/product/${rp.id}`)}>
                <img src={rp.image || LOGO_PH} alt="" />
                <div className="mpd-rec-name">{rp.name}</div>
                <div className="mpd-rec-price">{formatPriceRange(rp.priceMin, rp.priceMax)}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="mpd-spacer" />

      <div className="mpd-float-online">
        <img src={mainImg} alt="" />
        <span>Online</span>
        <span className="mpd-dot-g" />
      </div>

      <footer className="mpd-bar">
        <button type="button" className="mpd-bar-cart" onClick={handleAddCart}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
            <circle cx="9" cy="20" r="1.5" />
            <circle cx="17" cy="20" r="1.5" />
            <path d="M3 3h2l2 12h12l2-8H7" />
          </svg>
          <span>{isInInquiry(product.id) ? 'In cart' : 'Add to cart'}</span>
        </button>
        <button type="button" className="mpd-bar-chat" onClick={handleChat}>
          Chat now
        </button>
        <button type="button" className="mpd-bar-order" onClick={handleStartOrder}>
          Start order
        </button>
      </footer>

      <ChatModal isOpen={chatOpen} onClose={() => setChatOpen(false)} seller={seller} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} onSwitchToRegister={() => setLoginOpen(false)} />
      <UpgradePromptModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        feature={upgradeFeature}
        title="Upgrade required"
        message=""
      />
      <MobileProductVariationsModal
        open={variationsOpen}
        onClose={() => setVariationsOpen(false)}
        product={product}
        thumbUrl={images[0] || LOGO_PH}
        formatPrice={formatPrice}
        deliverCode={deliverCode}
        onConfirmAdd={handleVariationsConfirm}
      />
    </div>
  );
}
